import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/shared/UI";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { MessageSquare, Users } from "lucide-react";

export function NewChatDialog({ open, onClose, onCreated }) {
  const { user, users } = useAuth();
  const others = (users || []).filter((u) => u.id !== user?.id);
  const [type, setType] = useState("direct");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => { setType("direct"); setName(""); setSelected([]); };
  const toggle = (id) => setSelected((s) => (type === "direct" ? [id] : (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])));

  const create = async () => {
    if (selected.length === 0) return toast.error("Pick at least one person");
    if (type === "group" && !name.trim()) return toast.error("Give the group a name");
    setSaving(true);
    try {
      const { data } = await client.post("/chats", { type, member_ids: selected, name: type === "group" ? name.trim() : null });
      onCreated && onCreated(data);
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New chat</DialogTitle>
          <DialogDescription className="sr-only">Start a direct or group chat</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["direct", "Direct", MessageSquare], ["group", "Group", Users]].map(([val, label, Icon]) => (
              <button key={val} onClick={() => { setType(val); setSelected([]); }} data-testid={`new-chat-type-${val}`}
                className={cn("flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
                  type === val ? "border-sky-400 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50")}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          {type === "group" && (
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" data-testid="new-chat-group-name"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
          )}
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-1">
            {others.map((u) => (
              <button key={u.id} onClick={() => toggle(u.id)} data-testid={`new-chat-user-${u.id}`}
                className={cn("flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors",
                  selected.includes(u.id) ? "bg-sky-50 text-sky-800" : "hover:bg-slate-50 text-slate-700")}>
                <Avatar name={u.name} color={u.color} className="h-7 w-7 text-[11px]" />
                <span className="flex-1 truncate">{u.name}</span>
                <span className={cn("flex h-4 w-4 items-center justify-center rounded-full border", selected.includes(u.id) ? "border-sky-500 bg-sky-500 text-white" : "border-slate-300")}>
                  {selected.includes(u.id) && <span className="text-[9px]">✓</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="new-chat-create">
            {saving ? "Creating…" : "Start chat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
